import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Dialog,
  EmptyState,
  ErrorState,
  LoadingState,
  Field,
  Select,
  TextArea,
  TextInput,
  formatDate,
} from "@odingard/design-system";
import { useAuth } from "../../lib/auth";
import { api, reviewApi } from "../../lib/api";
import { useAsync } from "../../lib/useAsync";
import { interactionStateFor } from "./interactionStates";
import { InteractionStatePresenter } from "./interactionStatePresenter";
import { memberName, type ReviewMember } from "./memberNames";
import {
  CONCLUSION_OUTCOME_LABEL,
  FINDING_STATE_LABEL,
  LENS_STATE_LABEL,
  AGGREGATE_OUTCOME_LABEL,
  REVIEW_STATE_LABEL,
  SEVERITY_LABEL,
  stateLabel,
  stateTone,
} from "./labels";

export function ReviewDetail() {
  const { reviewId = "" } = useParams();
  const { activeOrg } = useAuth();
  const orgId = activeOrg?.id ?? "";
  const members = useAsync<ReviewMember[]>(async () => (await api.members(orgId)).members, [orgId]);
  const [outcomeOpen, setOutcomeOpen] = useState(false);
  const [outcome, setOutcome] = useState("APPROVED");
  const [rationale, setRationale] = useState("");
  const [saving, setSaving] = useState(false);
  const [actionError, setActionError] = useState<unknown>(null);
  const [scopeOpen, setScopeOpen] = useState(false);
  const [lensOpen, setLensOpen] = useState(false);
  const [transitionOpen, setTransitionOpen] = useState(false);
  const [scopeDocumentId, setScopeDocumentId] = useState("");
  const [scopeVersion, setScopeVersion] = useState("");
  const [lensKey, setLensKey] = useState("legal");
  const [reviewerId, setReviewerId] = useState("");
  const [editingLensId, setEditingLensId] = useState<string | null>(null);
  const [transitionTarget, setTransitionTarget] = useState("SCOPED");
  const [transitionReason, setTransitionReason] = useState("");
  const state = useAsync(
    async () => {
      const [reviewResponse, findingsResponse, outcomeResponse, transitionsResponse] = await Promise.all([
        reviewApi.getReview(orgId, reviewId),
        reviewApi.listFindings(orgId, reviewId),
        reviewApi.getOutcome(orgId, reviewId),
        reviewApi.listTransitions(orgId, reviewId),
      ]);
      const conclusionResponses = await Promise.all(
        reviewResponse.review.lenses.map((lens) => reviewApi.conclusionHistory(orgId, reviewId, lens.id)),
      );
      return {
        review: reviewResponse.review,
        findings: findingsResponse.findings,
        outcome: outcomeResponse.outcome,
        transitions: transitionsResponse.transitions,
        conclusions: conclusionResponses.flatMap((response) => response.conclusions),
      };
    },
    [orgId, reviewId],
  );
  if (state.loading && !state.data) return <LoadingState label="Loading review…" />;
  if (state.error) {
    const interactionState = interactionStateFor(state.error);
    return <InteractionStatePresenter state={interactionState} retry={state.reload} />;
  }
  const data = state.data;
  if (!data) return <ErrorState title="Review not found" />;
  const { review, findings, outcome: recordedOutcome, transitions } = data;
  const findingsByLens = new Map<string, typeof findings>();
  for (const finding of findings) {
    const current = findingsByLens.get(finding.lensId) ?? [];
    current.push(finding);
    findingsByLens.set(finding.lensId, current);
  }
  async function recordOutcome() {
    if (!rationale.trim()) return;
    setSaving(true);
    setActionError(null);
    try {
      await reviewApi.recordOutcome(orgId, reviewId, { outcome, rationale: rationale.trim() });
      setOutcomeOpen(false);
      setRationale("");
      state.reload();
    } catch (error) {
      setActionError(error);
    } finally {
      setSaving(false);
    }
  }
  async function addScope() {
    if (!scopeDocumentId.trim() || !scopeVersion) return;
    setSaving(true); setActionError(null);
    try {
      await reviewApi.addScopeItem(orgId, reviewId, { documentId: scopeDocumentId.trim(), version: Number(scopeVersion) });
      setScopeOpen(false); setScopeDocumentId(""); setScopeVersion(""); state.reload();
    } catch (error) { setActionError(error); } finally { setSaving(false); }
  }
  async function assignLens() {
    if (!reviewerId) return;
    setSaving(true); setActionError(null);
    try {
      if (editingLensId) {
        await reviewApi.reassignLens(orgId, reviewId, editingLensId, { reviewerId, reason: transitionReason.trim() || "Reviewer reassigned by accountable owner." });
      } else {
        await reviewApi.assignLens(orgId, reviewId, { lensKey, required: true, reviewerId });
      }
      setLensOpen(false); state.reload();
    } catch (error) { setActionError(error); } finally { setSaving(false); }
  }
  async function transition() {
    if (!transitionReason.trim()) return;
    setSaving(true); setActionError(null);
    try {
      await reviewApi.transition(orgId, reviewId, { toState: transitionTarget, reason: transitionReason.trim() });
      setTransitionOpen(false); setTransitionReason(""); state.reload();
    } catch (error) { setActionError(error); } finally { setSaving(false); }
  }
  const transitionOptions = review.lifecycleState === "REQUESTED"
    ? ["SCOPED", "CANCELLED"]
    : review.lifecycleState === "SCOPED" ? ["OPEN", "CANCELLED"] : [];
  return (
    <>
      <div className="page-head">
        <div>
          <p><Link to="/studio/review-ai">Review AI</Link> / Review object</p>
          <h1>{review.purpose}</h1>
          <p>Review identity: <code>{review.id}</code></p>
        </div>
        <div className="ods-row">
          <Badge tone={stateTone(review.lifecycleState)}>{stateLabel(review.lifecycleState, REVIEW_STATE_LABEL)}</Badge>
          {transitionOptions.length > 0 ? <Button size="sm" onClick={() => { setTransitionTarget(transitionOptions[0]!); setTransitionOpen(true); }}>Move lifecycle</Button> : null}
        </div>
        {transitionOptions.length === 0 ? <p role="status">Lifecycle actions are unavailable because this review is {stateLabel(review.lifecycleState, REVIEW_STATE_LABEL).toLowerCase()}.</p> : null}
      </div>
      <h2 className="ods-visually-hidden">Review details</h2>
      <Card>
        <CardHeader title="Review identity and governance" />
        <CardBody>
          <dl className="ods-detail-list">
            <div><dt>Requester</dt><dd>{memberName(members.data ?? [], review.requesterUserId)}</dd></div>
            <div><dt>Accountable owner</dt><dd>{memberName(members.data ?? [], review.ownerUserId)}</dd></div>
            <div><dt>Due date</dt><dd>{formatDate(review.dueAt)}</dd></div>
            <div><dt>Revision</dt><dd>{review.revision}</dd></div>
          </dl>
        </CardBody>
      </Card>
      <Card>
        <CardHeader title="Pinned scope" action={review.lifecycleState === "REQUESTED" || review.lifecycleState === "SCOPED" ? <Button size="sm" onClick={() => setScopeOpen(true)}>Pin document version</Button> : undefined} />
        <CardBody>
          {review.scopeItems.length > 0 ? <p>Each scope item is immutable after pinning. Findings must identify the pinned item they reference.</p> : null}
          {review.scopeItems.map((scope) => (
            <div key={scope.id} className="ods-row">
              <strong>{scope.documentId}</strong>
              <span>Version {scope.version}</span>
              <span>Checksum {scope.checksum}</span>
              {scope.page ? <span>Page {scope.page}</span> : null}
              {scope.section ? <span>Section {scope.section}</span> : null}
            </div>
          ))}
        </CardBody>
      </Card>
      <Card>
        <CardHeader title="Criteria" />
        <CardBody>Criteria set <strong>{review.criteriaSetId}</strong>, version <strong>{review.criteriaSetVersion}</strong>.</CardBody>
      </Card>
      <Card>
        <CardHeader title="Lenses" />
        <CardBody>
          {review.lifecycleState === "SCOPED" || review.lifecycleState === "OPEN" ? <Button size="sm" onClick={() => { setEditingLensId(null); setTransitionReason(""); setLensOpen(true); }}>Assign lens</Button> : null}
          {review.lenses.map((lens) => (
            <div key={lens.id} className="ods-row" style={{ justifyContent: "space-between", marginBottom: "var(--ods-space-2)" }}>
              <span>
                <strong>{lens.lensKey}</strong> · {memberName(members.data ?? [], lens.assignedReviewerId)} ·{" "}
                <Badge tone={stateTone(lens.state)}>{stateLabel(lens.state, LENS_STATE_LABEL)}</Badge>
              </span>
              <span className="ods-row">
                <Link to={`/studio/review-ai/reviews/${review.id}/lenses/${lens.id}`}><Button size="sm" variant="secondary">Open lens workspace</Button></Link>
                {review.lifecycleState !== "CONCLUDED" && lens.state !== "CONCLUDED" ? <Button size="sm" variant="secondary" onClick={() => { setEditingLensId(lens.id); setReviewerId(lens.assignedReviewerId ?? ""); setLensKey(lens.lensKey); setTransitionReason(""); setLensOpen(true); }}>Reassign</Button> : null}
              </span>
            </div>
          ))}
        </CardBody>
      </Card>
      <Card>
        <CardHeader title="Findings by lens" />
        <CardBody>
          {review.lenses.map((lens) => (
            <section key={lens.id} aria-labelledby={`findings-${lens.id}`} style={{ marginBottom: "var(--ods-space-3)" }}>
              <h3 id={`findings-${lens.id}`}>{lens.lensKey}</h3>
              {(findingsByLens.get(lens.id) ?? []).map((finding) => (
                <div key={finding.id} className="ods-card" style={{ marginBottom: "var(--ods-space-2)" }}>
                  <strong>{finding.title}</strong>
                  <p>{finding.description}</p>
                  <div className="ods-row">
                    <Badge tone={stateTone(finding.severity)}>{SEVERITY_LABEL[finding.severity] ?? finding.severity}</Badge>
                    <Badge tone={stateTone(finding.state)}>{stateLabel(finding.state, FINDING_STATE_LABEL)}</Badge>
                    <span>Raised by: {memberName(members.data ?? [], finding.raisedBy)}</span>
                    {finding.location ? (
                      <span>
                        Location: {finding.location.documentId}, version {finding.location.version}, checksum {finding.location.checksum}
                        {finding.location.page ? `, page ${finding.location.page}` : ""}
                        {finding.location.section ? `, section ${finding.location.section}` : ""}
                      </span>
                    ) : null}
                  </div>
                </div>
              ))}
            </section>
          ))}
        </CardBody>
      </Card>
      <Card>
        <CardHeader title="Outcome" action={<Button size="sm" onClick={() => setOutcomeOpen(true)} disabled={review.lifecycleState === "CONCLUDED"}>Record aggregate outcome</Button>} />
        <CardBody>
          {recordedOutcome ? (
            <p><Badge tone={stateTone(recordedOutcome.outcome)}>{stateLabel(recordedOutcome.outcome, AGGREGATE_OUTCOME_LABEL)}</Badge> · {recordedOutcome.recordedBy || "Not yet recorded"}</p>
          ) : <EmptyState title="No aggregate outcome" description="The review owner has not recorded an aggregate outcome." />}
        </CardBody>
      </Card>
      <Card>
        <CardHeader title="Lens conclusions" />
        <CardBody>
          {data.conclusions.length === 0 ? <EmptyState title="No lens conclusions" description="Assigned reviewers have not concluded a lens." /> : data.conclusions.map((conclusion) => (
            <div key={conclusion.id} className="ods-row">
              <strong>{review.lenses.find((lens) => lens.id === conclusion.lensId)?.lensKey ?? conclusion.lensId}</strong>
              <span>{stateLabel(conclusion.value, CONCLUSION_OUTCOME_LABEL)}</span>
              <span>{memberName(members.data ?? [], conclusion.concludedBy)}</span>
              <span>{formatDate(conclusion.concludedAt)}</span>
            </div>
          ))}
        </CardBody>
      </Card>
      <Card>
        <CardHeader title="History" />
        <CardBody>
          {transitions.map((transition) => (
            <div key={transition.id} className="ods-row">
              <span>{transition.fromState} → {transition.toState}</span>
              <span>{transition.reason}</span>
              <span>{memberName(members.data ?? [], transition.actorId)}</span>
              <span>{formatDate(transition.createdAt)}</span>
            </div>
          ))}
        </CardBody>
      </Card>
      <Dialog
        open={outcomeOpen}
        title="Record aggregate outcome"
        description={`You are acting as ${activeOrg?.name ?? "the current user"}. This governed action is recorded by Review AI.`}
        onClose={() => setOutcomeOpen(false)}
        dismissible={!saving}
        footer={
          <>
            <Button variant="secondary" onClick={() => setOutcomeOpen(false)} disabled={saving}>Cancel</Button>
            <Button onClick={recordOutcome} disabled={saving || !rationale.trim()}>{saving ? "Recording…" : "Record outcome"}</Button>
          </>
        }
      >
        <Field label="Outcome">{(id) => (
          <Select id={id} value={outcome} onChange={(event) => setOutcome(event.target.value)}>
            <option value="APPROVED">Approved</option>
            <option value="APPROVED_WITH_CONDITIONS">Approved with conditions</option>
            <option value="REJECTED">Rejected</option>
          </Select>
        )}</Field>
        <Field label="Rationale" help="A rationale is required for governed outcome recording.">
          {(id) => <TextArea id={id} rows={4} value={rationale} onChange={(event) => setRationale(event.target.value)} />}
        </Field>
        {actionError ? <InteractionStatePresenter state={interactionStateFor(actionError)} alternativeActor={memberName(members.data ?? [], review.ownerUserId)} /> : null}
      </Dialog>
      <Dialog open={scopeOpen} title="Pin document version"
        description={`You are acting as ${memberName(members.data ?? [], review.ownerUserId)}. Review AI will record the document version and checksum as immutable scope.`}
        onClose={() => setScopeOpen(false)} dismissible={!saving}
        footer={<><Button variant="secondary" onClick={() => setScopeOpen(false)}>Cancel</Button><Button onClick={addScope} disabled={saving || !scopeDocumentId.trim() || !scopeVersion}>{saving ? "Pinning…" : "Pin scope item"}</Button></>}>
        <Field label="Document identifier">{(id) => <TextInput id={id} value={scopeDocumentId} onChange={(e) => setScopeDocumentId(e.target.value)} />}</Field>
        <Field label="Document version">{(id) => <TextInput id={id} inputMode="numeric" value={scopeVersion} onChange={(e) => setScopeVersion(e.target.value)} />}</Field>
        <p>The backend resolves and records the checksum for this exact version. A pinned item cannot be edited or removed once the review opens.</p>
        {actionError ? <InteractionStatePresenter state={interactionStateFor(actionError)} alternativeActor={memberName(members.data ?? [], review.ownerUserId)} /> : null}
      </Dialog>
      <Dialog open={lensOpen} title={editingLensId ? "Reassign lens" : "Assign lens"}
        description={`You are acting as ${memberName(members.data ?? [], review.ownerUserId)}. Assignment is a governed review action.`}
        onClose={() => setLensOpen(false)} dismissible={!saving}
        footer={<><Button variant="secondary" onClick={() => setLensOpen(false)}>Cancel</Button><Button onClick={assignLens} disabled={saving || !reviewerId}>{saving ? "Saving…" : "Save assignment"}</Button></>}>
        <Field label="Lens">{(id) => <Select id={id} value={lensKey} onChange={(e) => setLensKey(e.target.value)}>{["legal", "security", "finance", "technical"].map((key) => <option key={key}>{key}</option>)}</Select>}</Field>
        <Field label="Reviewer">{(id) => <Select id={id} value={reviewerId} onChange={(e) => setReviewerId(e.target.value)}><option value="">Select a reviewer</option>{(members.data ?? []).map((member) => <option key={member.id} value={member.id}>{member.displayName}</option>)}</Select>}</Field>
        <Field label="Rationale" help="For reassignment, the backend requires a rationale.">{(id) => <TextArea id={id} rows={3} value={transitionReason} onChange={(e) => setTransitionReason(e.target.value)} />}</Field>
        {actionError ? <InteractionStatePresenter state={interactionStateFor(actionError)} alternativeActor={memberName(members.data ?? [], review.ownerUserId)} /> : null}
      </Dialog>
      <Dialog open={transitionOpen} title={`Move review to ${transitionTarget}`}
        description={`You are acting as ${memberName(members.data ?? [], review.ownerUserId)}. This governed lifecycle transition is recorded by Review AI.`}
        onClose={() => setTransitionOpen(false)} dismissible={!saving}
        footer={<><Button variant="secondary" onClick={() => setTransitionOpen(false)}>Cancel</Button><Button onClick={transition} disabled={saving || !transitionReason.trim()}>{saving ? "Saving…" : "Confirm transition"}</Button></>}>
        <Field label="Target state">{(id) => <Select id={id} value={transitionTarget} onChange={(e) => setTransitionTarget(e.target.value)}>{transitionOptions.map((value) => <option key={value}>{value}</option>)}</Select>}</Field>
        <Field label="Rationale" help="A rationale is required for every lifecycle transition.">{(id) => <TextArea id={id} rows={3} value={transitionReason} onChange={(e) => setTransitionReason(e.target.value)} />}</Field>
        {actionError ? <InteractionStatePresenter state={interactionStateFor(actionError)} alternativeActor={memberName(members.data ?? [], review.ownerUserId)} /> : null}
      </Dialog>
    </>
  );
}
